import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserCheck, Image, DollarSign, Plus, ShoppingBag, MousePointerClick, BadgeCheck, Check, X, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContentManager from "@/pages/admin/ContentManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const emptyOffer = { title: "", partner_name: "", description: "", category: "general",
  destination_url: "https://example.com/offer?subid={subid}", commission_user: 10, reward_type: "fixed",
  commission_partner: 0, reward_credits: 20, currency: "EUR",
  ready_caption: "", ready_image_url: "/premium-shop.jpg", ready_video_url: "", product_images: "", creatives: [],
  access_level: "free", starts_at: "", ends_at: "", cap: "", conditions: "", status: "active" };

export default function Admin() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("50");
  // affiliate
  const [affStats, setAffStats] = useState(null);
  const [offers, setOffers] = useState([]);
  const [sales, setSales] = useState([]);
  const [offerForm, setOfferForm] = useState(null);
  // video
  const [vidData, setVidData] = useState(null);

  const loadCore = async () => {
    const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
    setStats(s.data); setUsers(u.data.users);
  };
  const loadAff = async () => {
    const [as, of, sl] = await Promise.all([api.get("/admin/affiliate/stats"), api.get("/admin/offers"), api.get("/admin/affiliate/sales")]);
    setAffStats(as.data); setOffers(of.data.offers); setSales(sl.data.sales);
  };
  const loadVid = async () => { const r = await api.get("/admin/video/templates"); setVidData(r.data); };
  useEffect(() => { loadCore(); loadAff(); loadVid(); }, []);

  const confirmAdd = async () => {
    const n = parseInt(amount, 10);
    if (isNaN(n)) { toast.error(t("err_generic")); return; }
    await api.post(`/admin/users/${target.id}/credits`, { amount: n });
    toast.success(t("saved")); setTarget(null); loadCore();
  };

  const saveOffer = async () => {
    try {
      const product_images = String(offerForm.product_images || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      const payload = { ...offerForm, product_images,
        commission_user: parseFloat(offerForm.commission_user),
        commission_partner: parseFloat(offerForm.commission_partner) || 0,
        reward_credits: parseInt(offerForm.reward_credits, 10) || 0,
        cap: offerForm.cap === "" || offerForm.cap === null ? null : parseInt(offerForm.cap, 10),
        starts_at: offerForm.starts_at || null, ends_at: offerForm.ends_at || null };
      const id = offerForm.id;
      delete payload.id;
      if (id) await api.put(`/admin/offers/${id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success(t("saved")); setOfferForm(null); loadAff();
    } catch (e) { toast.error(t("err_generic")); }
  };
  const editOffer = (o) => setOfferForm({ ...emptyOffer, ...o, product_images: (o.product_images || []).join("\n"), creatives: (o.creatives || []),
    starts_at: (o.starts_at || "").slice(0, 16), ends_at: (o.ends_at || "").slice(0, 16), cap: o.cap == null ? "" : o.cap });
  const deleteOffer = async (o) => { await api.delete(`/admin/offers/${o.id}`); toast.success(t("saved")); loadAff(); };
  const fmtReward = (v, o) => o.reward_type === "percent" ? `${v} %` : `${({ EUR: "€", USD: "$" }[o.currency] || "€")}${v}`;
  const addCreative = () => setOfferForm((f) => ({ ...f, creatives: [...(f.creatives || []), { label: `Variante ${(f.creatives?.length || 0) + 1}`, type: "video", video_url: "", image_url: "", caption: "" }] }));
  const updateCreative = (i, k, v) => setOfferForm((f) => ({ ...f, creatives: f.creatives.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const removeCreative = (i) => setOfferForm((f) => ({ ...f, creatives: f.creatives.filter((_, idx) => idx !== i) }));
  const setSaleStatus = async (id, status) => {
    await api.put(`/admin/affiliate/sales/${id}`, { status });
    toast.success(t("saved")); loadAff();
  };

  const toggleTpl = async (tpl) => {
    await api.put(`/admin/video/templates/${tpl.id}`, { is_active: !tpl.is_active });
    loadVid();
  };

  if (!stats) return <div className="max-w-6xl mx-auto px-6 py-20 text-center text-stone-400 animate-pulse">{t("loading")}</div>;

  const cards = [
    { icon: Users, label: t("admin_users"), value: stats.total_users, color: "text-sky-600 bg-sky-100" },
    { icon: UserCheck, label: t("admin_verified"), value: stats.verified_users, color: "text-emerald-600 bg-emerald-100" },
    { icon: Image, label: t("admin_creations"), value: stats.total_creations, color: "text-violet-600 bg-violet-100" },
    { icon: DollarSign, label: t("admin_cost"), value: `$${stats.total_usd}`, color: "text-rose-600 bg-rose-100" },
  ];
  const affCards = affStats ? [
    { icon: ShoppingBag, label: "Offers", value: affStats.offers, color: "text-violet-600 bg-violet-100" },
    { icon: MousePointerClick, label: "Clicks", value: affStats.clicks, color: "text-sky-600 bg-sky-100" },
    { icon: BadgeCheck, label: "Confirmed", value: affStats.confirmed, color: "text-emerald-600 bg-emerald-100" },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-8">{t("admin_title")}</h1>
      <Tabs defaultValue="core">
        <TabsList className="rounded-full bg-stone-100 p-1 mb-6">
          <TabsTrigger value="core" className="rounded-full data-[state=active]:bg-white font-semibold px-5">Users & costs</TabsTrigger>
          <TabsTrigger value="aff" data-testid="admin-tab-aff" className="rounded-full data-[state=active]:bg-white font-semibold px-5">Affiliate</TabsTrigger>
          <TabsTrigger value="video" data-testid="admin-tab-video" className="rounded-full data-[state=active]:bg-white font-semibold px-5">Video AI</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content" className="rounded-full data-[state=active]:bg-white font-semibold px-5">Website content</TabsTrigger>
        </TabsList>

        <TabsContent value="core">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            {cards.map((c, i) => (
              <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-6" data-testid={`admin-stat-${i}`}>
                <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-3 ${c.color}`}><c.icon size={24} /></div>
                <p className="font-head font-black text-3xl">{c.value}</p>
                <p className="text-stone-500 font-medium">{c.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-3xl border-2 border-stone-200 overflow-x-auto">
            <table className="w-full text-left min-w-[640px]" data-testid="admin-users-table">
              <thead className="bg-stone-50 text-stone-500 text-sm">
                <tr><th className="px-5 py-3">Email</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Verified</th><th className="px-5 py-3">Credits</th><th className="px-5 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-5 py-3 font-medium">{u.email}</td>
                    <td className="px-5 py-3">{u.first_name}</td>
                    <td className="px-5 py-3">{u.role}</td>
                    <td className="px-5 py-3">{u.is_verified ? "✅" : "—"}</td>
                    <td className="px-5 py-3 font-bold text-primary">{u.credits}</td>
                    <td className="px-5 py-3">
                      <Button size="sm" variant="outline" onClick={() => { setTarget(u); setAmount("50"); }} data-testid={`add-credits-${u.id}`} className="rounded-full font-semibold"><Plus size={15} className="mr-1" />{t("admin_add_credits")}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="aff">
          <div className="grid grid-cols-3 gap-5 mb-6">
            {affCards.map((c, i) => (
              <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-6" data-testid={`admin-aff-stat-${i}`}>
                <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-3 ${c.color}`}><c.icon size={24} /></div>
                <p className="font-head font-black text-3xl">{c.value}</p>
                <p className="text-stone-500 font-medium">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-bold text-xl">Partner offers</h2>
            <Button onClick={() => setOfferForm({ ...emptyOffer })} data-testid="admin-create-offer" className="rounded-full bg-primary hover:bg-[#6D28D9] font-bold"><Plus size={16} className="mr-1" />New offer</Button>
          </div>
          <div className="bg-white rounded-3xl border-2 border-stone-200 overflow-x-auto mb-8" data-testid="admin-offers-table">
            <table className="w-full text-left min-w-[640px]">
              <thead className="bg-stone-50 text-stone-500 text-sm"><tr><th className="px-5 py-3">Offer</th><th className="px-5 py-3">Partner</th><th className="px-5 py-3">User reward</th><th className="px-5 py-3" title="Confidential BETOOL↔partner commission">Partner comm. 🔒</th><th className="px-5 py-3">Variants</th><th className="px-5 py-3">Credits</th><th className="px-5 py-3">Active</th><th className="px-5 py-3"></th></tr></thead>
              <tbody className="divide-y divide-stone-100">
                {offers.map((o) => (
                  <tr key={o.id} data-testid={`admin-offer-${o.id}`}>
                    <td className="px-5 py-3 font-medium">{o.title}</td>
                    <td className="px-5 py-3">{o.partner_name}</td>
                    <td className="px-5 py-3 font-bold text-primary">{fmtReward(o.commission_user, o)}</td>
                    <td className="px-5 py-3 text-stone-400 text-sm">{o.commission_partner ? fmtReward(o.commission_partner, o) : "—"}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 bg-primary/10 text-primary font-bold text-xs px-2 py-1 rounded-full">🎬 {(o.creatives || []).length}</span></td>
                    <td className="px-5 py-3">{o.reward_credits}</td>
                    <td className="px-5 py-3">{o.is_active ? "✅" : "—"}</td>
                    <td className="px-5 py-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => editOffer(o)} data-testid={`edit-offer-${o.id}`} className="rounded-full font-semibold"><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => deleteOffer(o)} data-testid={`delete-offer-${o.id}`} className="rounded-full text-red-500"><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="font-head font-bold text-xl mb-3">Sales</h2>
          <div className="bg-white rounded-3xl border-2 border-stone-200 overflow-x-auto" data-testid="admin-sales-table">
            <table className="w-full text-left min-w-[640px]">
              <thead className="bg-stone-50 text-stone-500 text-sm"><tr><th className="px-5 py-3">Offer</th><th className="px-5 py-3">Code</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr></thead>
              <tbody className="divide-y divide-stone-100">
                {sales.length === 0 && <tr><td colSpan="5" className="px-5 py-6 text-center text-stone-400">—</td></tr>}
                {sales.map((s) => (
                  <tr key={s.id}>
                    <td className="px-5 py-3 font-medium">{s.offer_title}</td>
                    <td className="px-5 py-3 font-mono text-sm">{s.code}</td>
                    <td className="px-5 py-3 font-bold text-emerald-600">€{s.amount}</td>
                    <td className="px-5 py-3">{s.status}</td>
                    <td className="px-5 py-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setSaleStatus(s.id, "confirmed")} data-testid={`confirm-sale-${s.id}`} className="rounded-full text-emerald-600"><Check size={15} /></Button>
                      <Button size="sm" variant="outline" onClick={() => setSaleStatus(s.id, "rejected")} data-testid={`reject-sale-${s.id}`} className="rounded-full text-red-500"><X size={15} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="video">
          {vidData && (
            <>
              <div className="grid grid-cols-3 gap-5 mb-6">
                {[["Generations", vidData.stats.generations], ["Done", vidData.stats.done], ["Failed", vidData.stats.failed]].map(([l, v], i) => (
                  <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-6">
                    <p className="font-head font-black text-3xl">{v}</p><p className="text-stone-500 font-medium">{l}</p>
                  </div>
                ))}
              </div>
              <h2 className="font-head font-bold text-xl mb-3">Video templates</h2>
              <div className="bg-white rounded-3xl border-2 border-stone-200 overflow-x-auto" data-testid="admin-video-templates">
                <table className="w-full text-left min-w-[560px]">
                  <thead className="bg-stone-50 text-stone-500 text-sm"><tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Category</th><th className="px-5 py-3">Credits</th><th className="px-5 py-3">Active</th><th className="px-5 py-3"></th></tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {vidData.templates.map((tp) => (
                      <tr key={tp.id}>
                        <td className="px-5 py-3 font-medium">{tp.name}</td>
                        <td className="px-5 py-3">{tp.category}</td>
                        <td className="px-5 py-3 font-bold text-primary">{tp.credits}</td>
                        <td className="px-5 py-3">{tp.is_active ? "✅" : "—"}</td>
                        <td className="px-5 py-3"><Button size="sm" variant="outline" onClick={() => toggleTpl(tp)} data-testid={`toggle-tpl-${tp.id}`} className="rounded-full font-semibold">{tp.is_active ? "Disable" : "Enable"}</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="content">
          <ContentManager />
        </TabsContent>
      </Tabs>

      {/* Add credits dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-head">{t("admin_add_credits")}</DialogTitle>
            <DialogDescription>{target?.email}</DialogDescription></DialogHeader>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credits-amount-input" className="min-h-[52px] rounded-xl border-2 border-stone-300 text-lg px-4" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} className="rounded-full">{t("back")}</Button>
            <Button onClick={confirmAdd} data-testid="confirm-add-credits" className="rounded-full bg-primary hover:bg-[#6D28D9] font-bold">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create offer dialog */}
      <Dialog open={!!offerForm} onOpenChange={(o) => !o && setOfferForm(null)}>
        <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{offerForm?.id ? "Edit offer" : "New partner offer"}</DialogTitle>
            <DialogDescription>Configure the offer shown to users.</DialogDescription></DialogHeader>
          {offerForm && (
            <div className="space-y-3">
              {[["title", "Title"], ["partner_name", "Partner name"], ["description", "Description"], ["category", "Category"], ["destination_url", "Destination URL (use {subid})"], ["ready_caption", "Ready caption"], ["ready_image_url", "Cover image URL"], ["ready_video_url", "Ready video URL (optional)"]].map(([k, l]) => (
                <div key={k}><Label className="text-sm font-semibold">{l}</Label>
                  {k === "description" || k === "ready_caption" ?
                    <Textarea value={offerForm[k]} onChange={(e) => setOfferForm({ ...offerForm, [k]: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1" data-testid={`offer-${k}`} /> :
                    <Input value={offerForm[k]} onChange={(e) => setOfferForm({ ...offerForm, [k]: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid={`offer-${k}`} />}
                </div>
              ))}
              <div>
                <Label className="text-sm font-semibold">Product images (real photos to feed video scenes — 1 per line)</Label>
                <Textarea value={offerForm.product_images} onChange={(e) => setOfferForm({ ...offerForm, product_images: e.target.value })} placeholder={"/premium-shop.jpg\nhttps://…/photo2.jpg"} className="rounded-xl border-2 border-stone-300 mt-1" data-testid="offer-product-images" rows={3} />
              </div>
              <div className="rounded-xl border-2 border-primary/25 bg-primary/5 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">🎬 Publish variants <span className="font-normal text-stone-500">(users are auto-assigned different ones)</span></Label>
                  <Button size="sm" variant="outline" onClick={addCreative} data-testid="add-creative" className="rounded-full font-semibold"><Plus size={14} className="mr-1" />Variant</Button>
                </div>
                {(offerForm.creatives || []).length === 0 && <p className="text-xs text-stone-500">No extra variant yet — the single caption/image/video above is used. Add variants so users don't all publish the same video.</p>}
                {(offerForm.creatives || []).map((c, i) => (
                  <div key={i} className="rounded-xl bg-white border border-stone-200 p-3 space-y-2" data-testid={`creative-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={c.label || ""} onChange={(e) => updateCreative(i, "label", e.target.value)} placeholder="Label" className="rounded-lg border-2 border-stone-300 min-h-[40px] flex-1" data-testid={`creative-label-${i}`} />
                      <select value={c.type || "video"} onChange={(e) => updateCreative(i, "type", e.target.value)} className="rounded-lg border-2 border-stone-300 min-h-[40px] px-2 bg-white text-sm" data-testid={`creative-type-${i}`}>
                        <option value="video">Video</option><option value="post">Post</option>
                      </select>
                      <Button size="sm" variant="outline" onClick={() => removeCreative(i)} data-testid={`remove-creative-${i}`} className="rounded-lg text-red-500"><Trash2 size={14} /></Button>
                    </div>
                    <Input value={c.video_url || ""} onChange={(e) => updateCreative(i, "video_url", e.target.value)} placeholder="Video URL" className="rounded-lg border-2 border-stone-300 min-h-[40px]" data-testid={`creative-video-${i}`} />
                    <Input value={c.image_url || ""} onChange={(e) => updateCreative(i, "image_url", e.target.value)} placeholder="Cover image URL" className="rounded-lg border-2 border-stone-300 min-h-[40px]" data-testid={`creative-image-${i}`} />
                    <Textarea value={c.caption || ""} onChange={(e) => updateCreative(i, "caption", e.target.value)} placeholder="Caption" className="rounded-lg border-2 border-stone-300" data-testid={`creative-caption-${i}`} rows={2} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm font-semibold">User reward type</Label>
                  <select value={offerForm.reward_type} onChange={(e) => setOfferForm({ ...offerForm, reward_type: e.target.value })} data-testid="offer-reward-type" className="w-full rounded-xl border-2 border-stone-300 mt-1 min-h-[46px] px-3 bg-white">
                    <option value="fixed">Fixed amount (€)</option>
                    <option value="percent">Percentage (%)</option>
                  </select>
                </div>
                <div><Label className="text-sm font-semibold">Currency</Label>
                  <select value={offerForm.currency} onChange={(e) => setOfferForm({ ...offerForm, currency: e.target.value })} data-testid="offer-currency" className="w-full rounded-xl border-2 border-stone-300 mt-1 min-h-[46px] px-3 bg-white">
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm font-semibold">User reward {offerForm.reward_type === "percent" ? "(%)" : "(€)"}</Label>
                  <Input type="number" value={offerForm.commission_user} onChange={(e) => setOfferForm({ ...offerForm, commission_user: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid="offer-commission" /></div>
                <div><Label className="text-sm font-semibold">Reward credits</Label>
                  <Input type="number" value={offerForm.reward_credits} onChange={(e) => setOfferForm({ ...offerForm, reward_credits: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" /></div>
              </div>
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-3">
                <Label className="text-sm font-semibold text-amber-800">🔒 Partner commission (CONFIDENTIAL — never shown to users)</Label>
                <Input type="number" value={offerForm.commission_partner} onChange={(e) => setOfferForm({ ...offerForm, commission_partner: e.target.value })} className="rounded-xl border-2 border-amber-300 mt-1 min-h-[46px] bg-white" data-testid="offer-commission-partner" />
              </div>
              <div className="rounded-xl border-2 border-stone-200 p-3 space-y-3">
                <Label className="text-sm font-semibold">Lifecycle & access</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-stone-500">Access level</Label>
                    <select value={offerForm.access_level} onChange={(e) => setOfferForm({ ...offerForm, access_level: e.target.value })} data-testid="offer-access-level" className="w-full rounded-xl border-2 border-stone-300 mt-1 min-h-[46px] px-3 bg-white">
                      <option value="free">Free</option><option value="pro">Pro</option><option value="master">Master</option>
                    </select>
                  </div>
                  <div><Label className="text-xs font-semibold text-stone-500">Status</Label>
                    <select value={offerForm.status} onChange={(e) => setOfferForm({ ...offerForm, status: e.target.value })} data-testid="offer-status" className="w-full rounded-xl border-2 border-stone-300 mt-1 min-h-[46px] px-3 bg-white">
                      <option value="active">Active</option><option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-stone-500">Starts at</Label>
                    <Input type="datetime-local" value={offerForm.starts_at} onChange={(e) => setOfferForm({ ...offerForm, starts_at: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid="offer-starts-at" /></div>
                  <div><Label className="text-xs font-semibold text-stone-500">Ends at</Label>
                    <Input type="datetime-local" value={offerForm.ends_at} onChange={(e) => setOfferForm({ ...offerForm, ends_at: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid="offer-ends-at" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-stone-500">Cap (max conversions, optional)</Label>
                    <Input type="number" value={offerForm.cap} onChange={(e) => setOfferForm({ ...offerForm, cap: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid="offer-cap" /></div>
                  <div><Label className="text-xs font-semibold text-stone-500">Conditions (shown to user)</Label>
                    <Input value={offerForm.conditions} onChange={(e) => setOfferForm({ ...offerForm, conditions: e.target.value })} className="rounded-xl border-2 border-stone-300 mt-1 min-h-[46px]" data-testid="offer-conditions" /></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferForm(null)} className="rounded-full">{t("back")}</Button>
            <Button onClick={saveOffer} data-testid="save-offer" className="rounded-full bg-primary hover:bg-[#6D28D9] font-bold">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
